import { sendAlarmBroadcast, type AlarmBroadcastDelivery, type AlarmBroadcastSendResponse, type SendAlarmBroadcastInput } from '@/shared/api/alarm-broadcast-client';
import { fetchCameraBroadcastDelivery, type CameraBroadcastStatus } from '@/shared/api/camera-broadcast-client';
import type { RecordedBroadcastResult, RecordedBroadcastStatus } from './recorded-broadcast-delivery';

export type SystemBroadcastRequest = { input: SendAlarmBroadcastInput; targetName: string };
export type SystemBroadcastFailure = { message: string; targetName: string };
export type SystemBroadcastRunResult = {
  failures: SystemBroadcastFailure[];
  responses: AlarmBroadcastSendResponse[];
};
export type SystemBroadcastCommandTracking = { fieldUid: string; requestScope: string; ids: string[] };

/** 부분 실패 재시도에서도 이미 등록한 명령의 상태 조회를 유지하고 중복 ID를 제거합니다. */
export function trackSystemBroadcastCommands(
  previous: SystemBroadcastCommandTracking,
  fieldUid: string,
  requestScope: string,
  run: SystemBroadcastRunResult
): SystemBroadcastCommandTracking {
  const previousIds = previous.fieldUid === fieldUid && previous.requestScope === requestScope ? previous.ids : [];
  return {
    fieldUid,
    // 완료된 송신 뒤 같은 방송을 새로 실행하면 이전 명령을 새 요청에 합치지 않습니다.
    requestScope: run.failures.length > 0 ? requestScope : '',
    ids: [...new Set([...previousIds, ...run.responses.flatMap(response => response.deliveries.flatMap(delivery => (delivery.command_id ? [delivery.command_id] : [])))])],
  };
}

const systemDeliveryApi = {
  poll: fetchCameraBroadcastDelivery,
  send: sendAlarmBroadcast,
  wait: (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)),
};

function statusMessage(status: CameraBroadcastStatus): string {
  if (status === 'preparing') return '음원 준비 중';
  if (status === 'accepted' || status === 'processing') return 'Media Server 재생 요청 완료';
  if (status === 'streaming') return '음성 재생 중';
  if (status === 'completed') return '음성 재생 끝';
  if (status === 'stopped') return '음성 재생 중지';
  if (status === 'cancelled') return '대기 중 취소';
  if (status === 'unknown') return '재생 결과 확인 불가';
  return 'Media Server 접수 후 재생 실패';
}

function immediateStatus(delivery: AlarmBroadcastDelivery): { message: string; status: RecordedBroadcastStatus } {
  if (delivery.status === 'QUEUED') return { status: 'queued', message: delivery.reason ?? 'IoT 방송 큐 등록 완료' };
  if (delivery.status === 'SENT') return { status: 'sent', message: delivery.reason ?? 'IoT 방송 전송 완료' };
  if (delivery.status === 'SKIPPED') return { status: 'skipped', message: delivery.reason ?? '방송 대상에서 제외됨' };
  if (delivery.status === 'FAILED') return { status: 'failed', message: delivery.reason ?? '방송 요청 실패' };
  if (delivery.status === 'UNKNOWN') return { status: 'unknown', message: delivery.reason ?? '방송 요청 결과 확인 불가' };
  if (delivery.status === 'ACCEPTED') return { status: 'accepted', message: delivery.reason ?? 'Media Server 재생 요청 완료' };
  return { status: 'unknown', message: delivery.reason ?? `알 수 없는 전송 상태: ${delivery.status}` };
}

/** 시스템 방송 요청 결과를 채널별로 표시하고 CCTV는 Media Server 종결 상태까지 조회합니다. */
export async function sendSystemBroadcast(
  requests: SystemBroadcastRequest[],
  onTargets: (targets: RecordedBroadcastResult[]) => void,
  onUpdate: (result: RecordedBroadcastResult) => void,
  isCancelled: () => boolean,
  api = systemDeliveryApi
): Promise<SystemBroadcastRunResult> {
  // 현장·권한 변경 시 다음 묶음의 송신을 중단하며 요청 수를 한 번에 네 개로 제한합니다.
  const requested: { request: SystemBroadcastRequest; response: Awaited<ReturnType<typeof api.send>> }[] = [];
  for (let offset = 0; offset < requests.length; offset += 4) {
    if (isCancelled()) return { responses: [], failures: [] };
    requested.push(
      ...(await Promise.all(
        requests.slice(offset, offset + 4).map(async request => ({
          request,
          response: await api.send(request.input),
        }))
      ))
    );
  }
  if (isCancelled()) return { responses: [], failures: [] };

  const responses = requested.flatMap(result => (result.response.ok ? [result.response.data] : []));
  const failures = requested.flatMap(result =>
    result.response.ok ? [] : [{ targetName: result.request.targetName, message: result.response.error?.message ?? '알람방송 전송에 실패했습니다.' }]
  );
  const tracked = responses.flatMap((response, responseIndex) =>
    response.deliveries
      .filter(delivery => delivery.channel === 'camera_external')
      .map((delivery, deliveryIndex) => {
        const identity = delivery.camera_uid ?? delivery.device_uid ?? `${responseIndex}-${deliveryIndex}`;
        const result = {
          uid: `${delivery.channel}:${identity}`,
          name: delivery.device_name,
          ...immediateStatus(delivery),
        } satisfies RecordedBroadcastResult;
        return { delivery, result };
      })
  );
  const initialResults = tracked.map(item => item.result);
  onTargets(initialResults);
  initialResults.forEach(onUpdate);

  await Promise.all(
    tracked.map(async ({ delivery, result }) => {
      if (delivery.channel !== 'camera_external' || delivery.status !== 'ACCEPTED') return;
      if (!delivery.camera_uid || !delivery.request_uid) {
        onUpdate({ ...result, status: 'unknown', message: 'CCTV 상태 조회 식별자를 받지 못했습니다.' });
        return;
      }
      let currentStatus: CameraBroadcastStatus = 'accepted';
      let pollAfterMs = 1_000;
      try {
        while (!['completed', 'failed', 'unknown', 'cancelled', 'stopped'].includes(currentStatus)) {
          await api.wait(pollAfterMs);
          if (isCancelled()) return;
          const polled = await api.poll(delivery.camera_uid, delivery.request_uid);
          if (!polled.ok) throw new Error(polled.error.message);
          currentStatus = polled.data.status;
          pollAfterMs = polled.data.poll_after_ms ?? 1_000;
          onUpdate({ ...result, status: currentStatus, message: polled.data.error_message ?? statusMessage(currentStatus) });
        }
      } catch (error) {
        onUpdate({
          ...result,
          status: 'unknown',
          message: error instanceof Error ? `Media Server 상태 조회 오류: ${error.message}` : 'Media Server 상태 조회 오류',
        });
      }
    })
  );

  return { responses, failures };
}
